import { Link } from 'react-router-dom';
import { CalendarPlus, CheckCircle2, RefreshCcw, XCircle } from 'lucide-react';
import { bookingIcsUrl } from '@/services/bookingService';
import { dublinDayLabel } from './DateTimeStep';

// Step 5 — confirmation. Rendered ONLY after the backend has genuinely
// stored the booking. Email honesty: the "we'll send confirmation" message
// appears only when a real email provider accepted the message; otherwise
// the booking details are shown on screen with a save-your-reference note.
export const ConfirmStep = ({ result }) => {
  const { reference, managementToken, booking, emailStatus } = result;
  const manageUrl = `/booking/manage/${reference}?token=${managementToken}`;

  return (
    <div data-testid="booking-confirmation">
      <div className="flex items-start gap-4">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-teal-50">
          <CheckCircle2 className="h-6 w-6 text-teal-700" aria-hidden="true" />
        </span>
        <div>
          <h3 className="text-2xl font-extrabold tracking-tight text-navy-900">Appointment confirmed</h3>
          <p className="mt-2 text-base leading-relaxed text-slate-600">
            {emailStatus === 'sent'
              ? 'Your appointment has been booked. We’ll send confirmation and joining details to the email address provided.'
              : 'Your appointment has been booked and stored. Please save your booking reference and the details below — online confirmation emails are being configured, so keep this page for your records.'}
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 bg-mist p-6 sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Booking reference</p>
        <p className="mt-1 font-mono text-2xl font-extrabold tracking-tight text-navy-900" data-testid="booking-reference">
          {reference}
        </p>
        <dl className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Meeting</dt>
            <dd className="mt-1 text-sm font-bold text-navy-900" data-testid="confirm-type">{booking.meetingType.name}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Date</dt>
            <dd className="mt-1 text-sm font-bold text-navy-900" data-testid="confirm-date">{dublinDayLabel(booking.date)}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Time</dt>
            <dd className="mt-1 text-sm font-bold text-navy-900" data-testid="confirm-time">
              {booking.startTime} – {booking.endTime} (Irish time)
            </dd>
          </div>
          <div>
            <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Format</dt>
            <dd className="mt-1 text-sm font-bold text-navy-900">{booking.location}</dd>
          </div>
        </dl>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
        <h4 className="text-sm font-extrabold uppercase tracking-[0.14em] text-navy-900">What happens next</h4>
        <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-slate-600">
          <li>Your appointment is confirmed in the Amden booking system.</li>
          <li>We’ll review your meeting context ahead of the conversation.</li>
          <li>Use the links below to add the appointment to your calendar, reschedule or cancel.</li>
        </ul>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <a
          href={bookingIcsUrl(reference, managementToken)}
          data-testid="booking-add-calendar"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
        >
          <CalendarPlus className="h-4 w-4" aria-hidden="true" />
          Add to Calendar
        </a>
        <Link
          to={`${manageUrl}#reschedule`}
          data-testid="booking-reschedule-link"
          className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-navy-900 px-6 py-3 text-sm font-bold text-navy-900 transition-colors hover:bg-navy-900 hover:text-white"
        >
          <RefreshCcw className="h-4 w-4" aria-hidden="true" />
          Reschedule
        </Link>
        <Link
          to={`${manageUrl}#cancel`}
          data-testid="booking-cancel-link"
          className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-slate-200 px-6 py-3 text-sm font-bold text-slate-600 transition-colors hover:border-red-200 hover:text-red-700"
        >
          <XCircle className="h-4 w-4" aria-hidden="true" />
          Cancel
        </Link>
      </div>
    </div>
  );
};
